import { useLoaderData, useLocation, useNavigate } from "@remix-run/react";
import { EventBrowser2 } from "~/components/layout/EventBrowser2";
import { IconFooter } from "~/components/layout/IconFooter";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { getHHRoleMap } from "~/.server/models/hh-roles";
import { getUpcomingEventSummaries } from "~/.server/models/hh-event2";
import type { HHEventSummaryRow } from "~/.server/models/hh-event2";
import { authenticator } from '~/.server/auth'
import { getAllCustomerData } from "~/.server/models/customer";
import type { RootLoaderData } from "~/root";
import { generateMeta } from "~/utils/meta";

export const meta: MetaFunction = ({ matches }) => {
    const rootData = matches[0].data as RootLoaderData;

    return generateMeta({
        title: "Upcoming Events",
        description: "Discover and connect with relevant people at professional events effortlessly with our SaaS tool. Enhance your networking and sales experiences and make meaningful connections with ease. Try it now!",
        keywords: "Professional events, SDR tool, event networking, find relevant people, event connections, professional networking software, sales research tool",
        baseUrl: rootData?.baseUrl ? `${rootData.baseUrl}/explore` : undefined,
        appTitle: rootData?.appTitle,
    });
};

export async function loader({ request }: LoaderFunctionArgs) {
    const user = await authenticator.isAuthenticated(request, {})
    const isLoggedIn = user !== null;
    let patron = null;
    if (isLoggedIn) {
        patron = await getAllCustomerData(user.id);
    }

    const roleMap = await getHHRoleMap();
    const roles = Array.from(roleMap.entries()).map(([id, label]) => ({ id, label }));

    const eventSummaries = await getUpcomingEventSummaries();

    // Convert event summaries to the format expected by the frontend
    const events = eventSummaries.map((summary: HHEventSummaryRow) => {
        // Parse the ISO date string and force Pacific Time
        const startDate = summary.start_time_iso ? new Date(summary.start_time_iso) : null;
        const PACIFIC_TIMEZONE = 'America/Los_Angeles';

        // Format the date for display using Pacific Time
        let displayDate = "TBD";
        if (startDate) {
            // Create dates in Pacific Time
            const pacificNow = new Date().toLocaleString("en-US", { timeZone: PACIFIC_TIMEZONE });
            const today = new Date(pacificNow);
            const tomorrow = new Date(pacificNow);
            tomorrow.setDate(tomorrow.getDate() + 1);

            // Compare dates in Pacific Time
            const startDateStr = startDate.toLocaleString("en-US", { timeZone: PACIFIC_TIMEZONE }).split(',')[0];
            const todayStr = today.toLocaleString("en-US", { timeZone: PACIFIC_TIMEZONE }).split(',')[0];
            const tomorrowStr = tomorrow.toLocaleString("en-US", { timeZone: PACIFIC_TIMEZONE }).split(',')[0];

            if (startDateStr === todayStr) {
                displayDate = "Today";
            } else if (startDateStr === tomorrowStr) {
                displayDate = "Tomorrow";
            } else {
                displayDate = startDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    timeZone: PACIFIC_TIMEZONE
                });
            }
        }

        // Format the time for display in Pacific Time
        const displayTime = startDate ? startDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: PACIFIC_TIMEZONE
        }) : "TBD";

        // Ensure rolePercentages is a Record<string, number>
        const rolePercentages: Record<string, number> = {};
        if (summary.role_to_percent_attendees && typeof summary.role_to_percent_attendees === 'object') {
            Object.entries(summary.role_to_percent_attendees).forEach(([role, percentage]) => {
                if (typeof percentage === 'number') {
                    rolePercentages[role] = Math.round(percentage * 100);
                }
            });
        }

        const inSF = summary.address?.includes("San Francisco");

        return {
            id: summary.url || crypto.randomUUID(), // Use URL as ID or generate one if not available
            title: summary.name || "Untitled Event",
            date: displayDate,
            time: displayTime,
            location: inSF ? "San Francisco, CA" : undefined,
            eventUrl: `https://${summary.url}` || "#",
            rolePercentages
        };
    });

    return json({ roles, events, isLoggedIn, patron });
}

export default function Explore() {
    const navigate = useNavigate();
    const location = useLocation();
    const { roles, events, isLoggedIn, patron } = useLoaderData<typeof loader>();

    return (
        <div className="flex flex-col h-screen">
            <div className="flex-1 pb-32 ml-4 mr-4 animate-in fade-in duration-1000 overflow-y-scroll">
                <EventBrowser2
                    roles={roles}
                    events={events}
                />
            </div>
            <div className="fixed bottom-0 w-full bg-background z-10">
                <IconFooter
                    icons={[
                        { id: 'explore', route: '/explore' },
                        { id: isLoggedIn ? 'my-events' : 'about', route: isLoggedIn && patron ? `/patrons/${patron.id}/events` : '/about' },
                        { id: 'support', route: '/support' },
                        { id: isLoggedIn ? 'account' : 'login', route: isLoggedIn && patron ? `/patrons/${patron.id}/settings` : '/login' }
                    ]}
                    selectedIcon="explore"
                    navigate={navigate}
                    location={location}
                />
            </div>
        </div>
    );
} 